import { SearchOutlined, Print } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { Col, Input, Row, Table } from 'antd';
import Loading from 'components/Loading';
import React, { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';

const isStandardExamReportUserActiveFromApi = (record) => record?.user?.active !== false;

const FilterTable = ({
  tableDataByClass,
  handleSearch,
  columns,
  number_of_questions,
  examUnitCompareColumns,
  examUnitCompareData,
  data,
}) => {
  const { isFeedBackShow } = data;
  const componentRef = useRef();
  const [printLoader, setPrintLoader] = useState(false);

  const activeTableRows = useMemo(
    () =>
      (Array.isArray(tableDataByClass) ? tableDataByClass : []).filter(
        isStandardExamReportUserActiveFromApi,
      ),
    [tableDataByClass],
  );

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Exam Report Table',
    pageStyle: '@page { size: A4 landscape; margin: 15mm; }',
    onBeforeGetContent: async () => setPrintLoader(true),
    onBeforePrint: async () => setPrintLoader(false),
  });

  const onPrint = () => {
    if (activeTableRows.length === 0) return;
    handlePrint();
  };

  return (
    <Row style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '20px' }}>
      <Col 
        xl={isFeedBackShow?.open ? 24 : 15} 
        lg={24} 
        md={24} 
        sm={24} 
        xs={24}
        style={{ 
          borderRight: !isFeedBackShow?.open ? '2px solid #e8e8e8' : 'none',
          paddingRight: !isFeedBackShow?.open ? '20px' : '0'
        }}
      >
        <TableContainer>
          <div style={{ width: '100%' }}>
            {isFeedBackShow?.open ? (
              <div className='width-full flex justify-end'>
                <IconButton
                  style={{ padding: '4px', backgroundColor: '#000000', marginBottom: "10px" }}
                  aria-label="print"
                  onClick={onPrint}
                >
                  {printLoader ? <Loading size={20} /> : <Print style={{ color: 'white' }} />}
                </IconButton>
              </div>
            ) : null}
            {isFeedBackShow?.open ? null : (
              <Row>
                <Col style={{ position: 'relative', width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '14px',
                    }}
                  >
                    <SearchBar
                      onSearch={handleSearch}
                      placeholder="Search by name"
                      style={{ flex: 1, marginRight: '10px' }}
                    />
                  </div>
                  <NoOfQuestions>Number of questions: {number_of_questions} </NoOfQuestions>
                </Col>
              </Row>
            )}

            <Row>
              <div style={{ width: '100%', overflowX: 'auto' }}>
                <CustomTable
                  columns={columns}
                  dataSource={activeTableRows}
                  pagination={{ position: 'bottomRight' }}
                />
              </div>
            </Row>
          </div>
        </TableContainer>
      </Col>
      <Col
        className="customizeTableRght"
        xl={isFeedBackShow?.open ? 0 : 9}
        lg={24}
        md={24}
        sm={24}
        xs={24}
        style={{
          paddingLeft: !isFeedBackShow?.open ? '20px' : '0'
        }}
      >
        {!isFeedBackShow?.open && examUnitCompareData?.length > 0 && (
          <TableContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '64px',
              }}
            >
              <Title>Overall Passing per Question</Title>
            </div>
            {isFeedBackShow?.open ? null : (
              <div style={{ width: '100%', overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 50px)' }}>
                <StyledTable
                  columns={examUnitCompareColumns}
                  dataSource={examUnitCompareData || []}
                  pagination={false}
                />
              </div>
            )}
          </TableContainer>
        )}
      </Col>

      {/* Hidden print component */}
      <div style={{ display: 'none' }} className="printable">
        <PrintComponent
          ref={componentRef}
          tableDataByClass={activeTableRows}
          columns={columns}
          examUnitCompareData={examUnitCompareData}
          examUnitCompareColumns={examUnitCompareColumns}
          number_of_questions={number_of_questions}
        />
      </div>
    </Row>
  );
};

// Print Component
const PrintComponent = React.forwardRef(
  (
    { tableDataByClass, columns, examUnitCompareData, examUnitCompareColumns, number_of_questions },
    ref,
  ) => {
    return (
      <PrintStyles>
        <div ref={ref} className="print-container printable">
          <div style={{ width: '100%', overflowX: 'auto', padding: 20 }}>
            <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Exam Report</h2>
            <div style={{ marginBottom: '30px' }}>
              <PrintTable dataSource={tableDataByClass} columns={columns} />
            </div>
          </div>
        </div>
      </PrintStyles>
    );
  },
);

// Print Table Component
const PrintTable = ({ dataSource, columns }) => {
  return (
    <PrintStyledTable>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <PrintTh key={index}>{column.title || column.key}</PrintTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {dataSource.map((record, rowIndex) => (
          <tr key={rowIndex}>
            {columns.map((column, colIndex) => (
              <PrintTd key={colIndex}>
                {column.render
                  ? column.render(record[column.dataIndex], record, rowIndex)
                  : record[column.dataIndex]}
              </PrintTd>
            ))}
          </tr>
        ))}
      </tbody>
    </PrintStyledTable>
  );
};

export default FilterTable;

const SearchBar = ({ onSearch }) => {
  return (
    <StyledSearchInput
      prefix={<SearchOutlined />}
      placeholder="Search by student name"
      onChange={(e) => onSearch(e.target.value)}
    />
  );
};

const Title = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: bold;
  margin-bottom: 10px;
`;

const ModernButton = styled.button`
  border-radius: 8px !important;
  border: none !important;
  cursor: pointer !important;
  outline: none !important;
  transition: all 0.2s ease !important;
  height: 44px !important;
  padding: 0 20px !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;

  &.primary {
    background: #3b82f6 !important;
    color: #ffffff !important;

    &:hover {
      background: #2563eb !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
    }
  }

  &.secondary {
    background: #f3f4f6 !important;
    color: #374151 !important;

    &:hover {
      background: #e5e7eb !important;
      transform: translateY(-1px) !important;
    }
  }
`;

const CustomTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: rgb(239, 246, 254);
    color: black;
  }
`;

const StyledTable = styled(Table)`
  && tbody > tr > td {
    text-align: center;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 20px;
`;

const StyledSearchInput = styled(Input)`
  height: 50px;
  margin-bottom: 14px;
  .ant-input-prefix {
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.25);
  }

  .ant-input {
    border-radius: 4px;
  }

  /* Add more styles if you need */
`;

const NoOfQuestions = styled.span`
  position: absolute;
  right: 10px;
  top: 14px;
  z-index: 0;
  @media screen and (max-width: 400px) {
    font-size: 12px;
    top: 16px;
  }
`;

const PrintStyles = styled.div`
  @media print {
    @page {
      size: A4 landscape;
      margin: 15mm;
    }

    body,
    html {
      width: 100%;
      height: auto;
      overflow: visible !important;
    }

    .print-container {
      width: 100%;
      height: auto !important;
      overflow: visible !important;
      position: relative;
    }

    .printable {
      width: 100%;
      height: auto;
    }

    table,
    tr,
    td,
    th {
      page-break-inside: avoid;
    }

    thead {
      display: table-header-group;
    }

    tfoot {
      display: table-footer-group;
    }
  }

  .print-container {
    width: 100%;
    padding: 20px;
    box-sizing: border-box;
  }

  h2 {
    margin-bottom: 20px;
  }

  h3 {
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 10px;
  }
`;

const PrintStyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    word-wrap: break-word;
    white-space: normal;
  }

  th {
    background-color: #f2f2f2;
    font-weight: bold;
    color: black;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  @media print {
    th,
    td {
      border: 1px solid #000;
      padding: 6px;
      font-size: 12px;
    }

    th {
      background-color: #f2f2f2 !important;
      color: black !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }

    tr:nth-child(even) {
      background-color: #f9f9f9 !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }
  }
`;

const PrintTh = styled.th`
  background-color: #f2f2f2;
  font-weight: bold;
  color: black;
`;

const PrintTd = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
`;
